import { API, BACKEND_URL } from '@/config/api';
import { useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

/**
 * Custom hook for handling initial data loading
 * Manages fetching areas, locations, and loading bulk registrations for editing
 */
export const useInitialDataLoading = ({
  bulkId,
  isNewRegistration,
  draftIdFromUrl,
  navigate,
  setIsEditMode,
  setAreas,
  setAllAvailableLocations,
  setSampleDate,
  setSelectedArea,
  setGridData,
  setZooplanktonGridData,
  setColorOverrides,
  setTemperatureData,
  setEditableSummary,
  setGeneratedSummary,
  setCurrentLocations,
  setSelectedTemplate,
  setCurrentZooplanktonOrganisms,
  setOrganisms,
  loadDraft,
  isPastingRef,
  confirmDialog
}) => {
  const hasMountedRef = useRef(false);
  const hasLoadedDraftRef = useRef(false);
  
  // Fetch areas and locations
  const fetchAreas = useCallback(async () => {
    try {
      const [areasRes, locationsRes] = await Promise.all([
        axios.get(`${API}/areas`, { withCredentials: true }),
        axios.get(`${API}/locations`, { withCredentials: true })
      ]);
      setAreas(areasRes.data);
      setAllAvailableLocations(locationsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      // Fallback to mock data if API fails
      setAreas([
        { 
          id: 'p02-rogaland', 
          name: 'P02 Rogaland', 
          region: 'Rogaland', 
          companies: ['Mowi', 'Lerøy Seafood', 'SalMar', 'Grieg Seafood'] 
        },
        { 
          id: 'p03-rogaland', 
          name: 'P03 Rogaland', 
          region: 'Rogaland', 
          companies: ['Cermaq', 'Nova Sea'] 
        }
      ]);
      
      const mockLocations = [
        { id: 'loc-a', letter: 'A', name: 'Toftøy', area_id: 'p02-rogaland' },
        { id: 'loc-b', letter: 'B', name: 'Føresvik', area_id: 'p02-rogaland' },
        { id: 'loc-c', letter: 'C', name: 'Rosshl.', area_id: 'p02-rogaland' },
        { id: 'loc-d', letter: 'D', name: 'Høgsfj.', area_id: 'p02-rogaland' },
        { id: 'loc-e', letter: 'E', name: 'Finnøyfj.', area_id: 'p02-rogaland' },
        { id: 'loc-f', letter: 'F', name: 'Årdalsfj.', area_id: 'p02-rogaland' },
        { id: 'loc-g', letter: 'G', name: 'Jøsenfj.', area_id: 'p02-rogaland' },
        { id: 'loc-h', letter: 'H', name: 'Erfj.', area_id: 'p02-rogaland' },
        { id: 'loc-i', letter: 'I', name: 'Jelsa', area_id: 'p02-rogaland' },
        { id: 'loc-j', letter: 'J', name: 'Vindafj.', area_id: 'p02-rogaland' },
        { id: 'loc-k', letter: 'K', name: 'Rott', area_id: 'p02-rogaland' },
        { id: 'loc-l', letter: 'L', name: 'Boknafj.', area_id: 'p02-rogaland' }
      ];
      setAllAvailableLocations(mockLocations);
      toast.error('Kunne ikke hente områder og lokaliteter');
    }
  }, [setAreas, setAllAvailableLocations]);

  // Load bulk registration for editing
  const loadBulkRegistration = useCallback(async (id) => {
    try {
      const res = await axios.get(`${API}/bulk-registrations`, { withCredentials: true });
      const bulk = res.data.find(b => b.id === id);
      
      if (bulk) {
        
        // Set date
        if (bulk.date) {
          // Parse date as local (not UTC) to avoid timezone shift
          const [y, m, d] = bulk.date.split('-').map(Number);
          setSampleDate(new Date(y, m - 1, d));
        }
        
        // Set area
        if (bulk.area_id) {
          setSelectedArea(bulk.area_id);
        }
        
        // Find matching template for this area's locations
        let matchedTemplate = null;
        let templateLocations = [];
        if (setSelectedTemplate) {
          try {
            const templatesRes = await axios.get(`${API}/bulk-templates`, { withCredentials: true });
            const templates = templatesRes.data || [];
            const areaName = (bulk.area_name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
            
            // Match by area name
            matchedTemplate = templates.find(t => {
              const tName = (t.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
              return areaName && tName && (tName.includes(areaName) || areaName.includes(tName));
            });
            if (matchedTemplate) {
              setSelectedTemplate(matchedTemplate.id);
              templateLocations = (matchedTemplate.config?.locations || []).map(l => l.full_name);
            }
          } catch {}
        }
        
        // Set grid data
        if (bulk.algae_data) {
          setGridData(bulk.algae_data);
          
          // Use saved current_locations if available (respects deleted columns)
          // Fall back to template locations, then to extracting from grid data
          const savedLocations = bulk.current_locations || [];
          if (savedLocations.length > 0 && setCurrentLocations) {
            setCurrentLocations(savedLocations);
          } else if (templateLocations.length > 0 && setCurrentLocations) {
            setCurrentLocations(templateLocations);
          } else {
            // Extract locations from algae data
            for (const orgData of Object.values(bulk.algae_data)) {
              if (orgData && typeof orgData === 'object') {
                const locs = Object.keys(orgData);
                if (locs.length > 0 && setCurrentLocations) {
                  setCurrentLocations([...new Set(locs)]);
                  break;
                }
              }
            }
          }
          
          // Extract and reconstruct organisms from saved algae_data keys with thresholds
          if (setOrganisms) {
            try {
              const algaeRes = await axios.get(`${API}/organisms/algae-structure`, { withCredentials: true });
              const backendOrganisms = algaeRes.data;
              
              // Build threshold lookup maps
              const thresholdMap = {};
              const groupThresholdMap = {};
              Object.entries(backendOrganisms).forEach(([group, groupData]) => {
                const speciesList = Array.isArray(groupData) ? groupData : (groupData?.species || []);
                speciesList.forEach(sp => {
                  thresholdMap[`${group}_${sp.species}`] = {
                    threshold_green: sp.threshold_green,
                    threshold_yellow: sp.threshold_yellow,
                    threshold_orange: sp.threshold_orange,
                    threshold_red: sp.threshold_red,
                    depth: sp.depth,
                    biovolume_classes: sp.biovolume_classes || []
                  };
                });
                const gt = groupData?.group_thresholds;
                if (gt) {
                  groupThresholdMap[group] = {
                    threshold_yellow: gt.threshold_yellow,
                    threshold_orange: gt.threshold_orange,
                    threshold_red: gt.threshold_red
                  };
                }
              });
              
              // Reconstruct organisms from grid data keys — only those with data or in template
              const reconstructed = [];
              const seenGroups = new Set();
              
              // Get template organisms for filtering
              let templateOrgKeys = new Set();
              try {
                const tmplRes = await axios.get(`${API}/bulk-templates`, { withCredentials: true });
                const templates = tmplRes.data || [];
                const aName = (bulk.area_name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                const matchedTmpl = templates.find(t => {
                  const tName = (t.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                  return aName && tName && (tName.includes(aName) || aName.includes(tName));
                });
                if (matchedTmpl?.config?.organisms) {
                  matchedTmpl.config.organisms.forEach(o => templateOrgKeys.add(`${o.group}_${o.species}`));
                }
              } catch {}
              
              Object.keys(bulk.algae_data).forEach(key => {
                const parts = key.split('_');
                const group = parts[0];
                const species = parts.slice(1).join('_');
                const isSum = species.includes(' SUM');
                
                // Skip empty organisms that aren't in the template
                if (!isSum) {
                  const locData = bulk.algae_data[key];
                  const hasData = locData && Object.values(locData).some(v => {
                    const s = String(v || '').trim();
                    return s !== '' && s !== '0' && s !== '—';
                  });
                  if (!hasData && !templateOrgKeys.has(key)) return;
                }
                
                const thresholds = isSum ? (groupThresholdMap[group] || {}) : (thresholdMap[key] || {});
                
                reconstructed.push({
                  group,
                  species,
                  depth: thresholds.depth || '',
                  isSpecies: !isSum,
                  isSum,
                  ...thresholds
                });
                seenGroups.add(group);
              });
              
              if (reconstructed.length > 0) {
                setOrganisms(reconstructed);
              }
            } catch (error) {
              console.error('Error fetching thresholds for edit mode:', error);
            }
          }
        }
        
        // Set zooplankton data + organisms from TEMPLATE (not from saved data keys)
        if (bulk.zooplankton_data) {
          setZooplanktonGridData(bulk.zooplankton_data);
          
          // Use the matched template's zooplankton as the organism list
          // This ensures the template defines the structure, saved data just provides values
          if (setCurrentZooplanktonOrganisms) {
            try {
              const templatesRes2 = await axios.get(`${API}/bulk-templates`, { withCredentials: true });
              const matchedTemplate = templatesRes2.data?.find(t => {
                const tName = (t.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                const aName = (bulk.area_name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                return aName && tName && (tName.includes(aName) || aName.includes(tName));
              });
              const templateZoo = matchedTemplate?.config?.zooplankton;
              
              if (templateZoo && templateZoo.length > 0) {
                // Start with template organisms
                const zooOrganisms = templateZoo.map(z => ({
                  sampleType: z.sampleType, organism: z.organism, isSpecies: true, default_value: z.default_value || '<5'
                }));
                const templateKeys = new Set(templateZoo.map(z => `${z.sampleType}_${z.organism}`));
                
                // Also add any organisms from saved data that have actual values but aren't in template (manually added)
                Object.entries(bulk.zooplankton_data).forEach(([key, locData]) => {
                  if (!templateKeys.has(key)) {
                    const hasData = Object.values(locData || {}).some(v => v && String(v).trim() !== '' && String(v).trim() !== '0');
                    if (hasData) {
                      const parts = key.split('_');
                      zooOrganisms.push({ sampleType: parts[0], organism: parts.slice(1).join('_'), isSpecies: true, isCustom: true });
                    }
                  }
                });
                
                setCurrentZooplanktonOrganisms(zooOrganisms);
              } else {
                // No template match — fall back to saved data keys that have values
                const zooOrganisms = Object.keys(bulk.zooplankton_data).map(key => {
                  const parts = key.split('_');
                  return { sampleType: parts[0], organism: parts.slice(1).join('_'), isSpecies: true };
                });
                if (zooOrganisms.length > 0) setCurrentZooplanktonOrganisms(zooOrganisms);
              }
            } catch {
              // Fallback — use saved data keys
              const zooOrganisms = Object.keys(bulk.zooplankton_data).map(key => {
                const parts = key.split('_');
                return { sampleType: parts[0], organism: parts.slice(1).join('_'), isSpecies: true };
              });
              if (zooOrganisms.length > 0) setCurrentZooplanktonOrganisms(zooOrganisms);
            }
          }
        }
        
        // Set color overrides
        if (bulk.color_overrides) {
          setColorOverrides(bulk.color_overrides);
        }
        
        // Set temperature data
        if (bulk.temperature_data && setTemperatureData) {
          setTemperatureData(bulk.temperature_data);
        }
        
        // Set summary if exists
        if (bulk.ai_summary) {
          setEditableSummary(bulk.ai_summary);
          setGeneratedSummary(bulk.ai_summary);
        }
        
        toast.success('Bulk-registrering lasta inn for redigering');
        
        // Warn if editing a bulk that has been sent to customer (skip for drafts)
        if ((bulk.sent_to_customer || bulk.status === 'sent') && bulk.status !== 'draft') {
          if (confirmDialog) {
            setTimeout(async () => {
              const ok = await confirmDialog({
                title: 'Redigere sendt prøve?',
                message: 'Denne prøven er allereie sendt til kunde. Endringar du gjer vil oppdatere originalen og ein ny versjon vert lagra.',
                confirmText: 'Ja, rediger',
                cancelText: 'Gå tilbake',
                variant: 'warning'
              });
              if (!ok) navigate('/admin/bulk-history');
            }, 300);
          }
        }
      } else {
        toast.error('Fant ikkje bulk-registrering');
        navigate('/admin/bulk-history');
      }
    } catch (error) {
      console.error('Error loading bulk registration:', error);
      toast.error('Feil ved lasting av bulk-registrering');
      navigate('/admin/bulk-history');
    }
  }, [
    navigate,
    setSampleDate,
    setSelectedArea,
    setGridData,
    setZooplanktonGridData,
    setColorOverrides,
    setEditableSummary,
    setGeneratedSummary
  ]);

  // Initial data loading on mount
  useEffect(() => {
    // Prevent double execution (React StrictMode + hot reload)
    if (hasMountedRef.current) return;
    hasMountedRef.current = true;
    
    fetchAreas();
    // fetchOrganisms is called separately from BulkSampleEntry component to avoid circular dependencies
    
    // Check if we're in edit mode
    if (bulkId) {
      setIsEditMode(true);
      loadBulkRegistration(bulkId);
    } else {
      // Check if this is explicitly a "new" registration (via ?new=1 param from "Ny prøve" button)
      if (isNewRegistration) {
        // Clean the URL param so refresh will load the draft
        window.history.replaceState({}, '', window.location.pathname);
        // Start blank — don't load any draft
      } else if (draftIdFromUrl) {
        // Specific draft requested via ?draft=ID (from "Fortsett" button)
        hasLoadedDraftRef.current = true;
        loadDraft(draftIdFromUrl);
      } else if (!hasLoadedDraftRef.current) {
        // Refresh or direct navigation — load today's draft if exists
        if (isPastingRef && isPastingRef.current) return;
        hasLoadedDraftRef.current = true;

        (async () => {
          try {
            const response = await axios.get(`${API}/bulk-drafts`, { withCredentials: true });
            if (response.data && response.data.length > 0) {
              const today = new Date().toISOString().split('T')[0];
              const sortedDrafts = response.data.sort((a, b) => {
                const dateA = new Date(a.updated_at || a.created_at);
                const dateB = new Date(b.updated_at || b.created_at);
                return dateB - dateA;
              });
              const latest = sortedDrafts[0];
              const updatedAt = latest.updated_at || latest.created_at;
              const draftDay = updatedAt ? new Date(updatedAt).toISOString().split('T')[0] : null;

              if (draftDay === today) {
                loadDraft();
              }
            }
          } catch {}
        })();
      }
    }
    
    // IMPLEMENTATION DETAIL: Window.focus listener was removed because:
    // - It triggers when closing dialogs (confirm, modals)
    // - This overwrites user's deletions
    // - Instead, users can manually click "Oppdater organismer" button
  }, [bulkId]);

  return { fetchAreas, loadBulkRegistration };
};
